/**
 * header.js — sections/header.liquid only.
 *
 * Two responsibilities:
 *   1. Scrolled-nav state — an IntersectionObserver on a 1px sentinel at the
 *      top of the page toggles `[data-scrolled]` on the header. No scroll
 *      handler, no `getBoundingClientRect` polling (GOTCHAS: "Scroll-linked
 *      JS kills performance").
 *   2. Mobile menu — accessible disclosure: `aria-expanded`, Escape closes
 *      and returns focus to the toggle, click-outside closes, focus moves
 *      into the panel's first link on open.
 *
 * FAIL-SOFT: if IntersectionObserver is unsupported, the header simply never
 * gains the scrolled style (cosmetic only). If JS fails entirely, the mobile
 * panel's `hidden` attribute default keeps it closed but the desktop nav
 * links remain in the DOM and usable at any viewport via the `.site-header__nav`
 * (hidden only above 900px via CSS) — never a dead nav.
 *
 * Loaded as `<script type="module" defer>` — no globals, no dependencies.
 */

const LOG_PREFIX = "[BecomeFounder]";

function initScrolledState() {
  const header = document.querySelector("[data-site-header]");
  const sentinel = document.querySelector("[data-header-sentinel]");
  if (!header || !sentinel || !("IntersectionObserver" in window)) return;

  try {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            header.removeAttribute("data-scrolled");
          } else {
            header.setAttribute("data-scrolled", "true");
          }
        });
      },
      { threshold: 0 }
    );
    observer.observe(sentinel);
  } catch (err) {
    console.warn(`${LOG_PREFIX} header scrolled-state observer failed`, err);
  }
}

/**
 * M8 — nav band awareness. The nav is a persistent dark chip that never
 * flips theme itself (DESIGN-DIRECTION §1/§6), but on scroll it condenses
 * into a floating pill (header.css `[data-scrolled]`) and, specifically
 * over LIGHT bands, that pill gets side margins + full rounding so it never
 * reads as "a black bar over cream" (DESIGN-DIRECTION §4.4 M8). This only
 * sets a `data-band-theme` attribute from each `.band[data-theme]`'s own
 * (already-correct) Liquid-rendered value — one IO instance, no scroll
 * handler, no layout reads inside the callback (GOTCHAS "Scroll-linked JS
 * kills performance"). `header.offsetHeight` is read once at init, not in
 * the callback.
 */
function initBandAwareness() {
  const header = document.querySelector("[data-site-header]");
  const bands = document.querySelectorAll(".band[data-theme]");
  if (!header || !bands.length || !("IntersectionObserver" in window)) return;

  try {
    const headerHeight = header.offsetHeight || 72;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const theme = entry.target.getAttribute("data-theme");
            if (theme) header.setAttribute("data-band-theme", theme);
          }
        });
      },
      { rootMargin: `-${headerHeight}px 0px -85% 0px`, threshold: 0 }
    );
    bands.forEach((band) => observer.observe(band));
  } catch (err) {
    console.warn(`${LOG_PREFIX} header band-awareness observer failed`, err);
  }
}

function initMobileMenu() {
  const header = document.querySelector("[data-site-header]");
  if (!header) return;

  const toggle = header.querySelector("[data-header-toggle]");
  const panel = header.querySelector("[data-header-mobile]");
  if (!toggle || !panel) return;

  function openMenu() {
    panel.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    const firstLink = panel.querySelector("a, button");
    if (firstLink) firstLink.focus();
    document.addEventListener("keydown", onKeydown);
    document.addEventListener("click", onDocumentClick, true);
  }

  function closeMenu(returnFocus) {
    panel.hidden = true;
    toggle.setAttribute("aria-expanded", "false");
    document.removeEventListener("keydown", onKeydown);
    document.removeEventListener("click", onDocumentClick, true);
    if (returnFocus) toggle.focus();
  }

  function onKeydown(event) {
    if (event.key === "Escape") {
      closeMenu(true);
    }
  }

  function onDocumentClick(event) {
    if (!header.contains(event.target)) {
      closeMenu(false);
    }
  }

  toggle.addEventListener("click", () => {
    const isOpen = toggle.getAttribute("aria-expanded") === "true";
    if (isOpen) {
      closeMenu(false);
    } else {
      openMenu();
    }
  });

  panel.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => closeMenu(false));
  });
}

function init() {
  try {
    initScrolledState();
    initBandAwareness();
    initMobileMenu();
  } catch (err) {
    console.warn(`${LOG_PREFIX} header.js init failed — continuing without enhancement`, err);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
